import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { facts, categories, verdictMeta, categoryName } from "@/mocks/articles";
import { ArticleCard, VerdictBadge } from "@/components/base/ArticleCard";
import Breadcrumb, { Footer } from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import JsonLd from "@/components/base/JsonLd";
import useSeo from "@/hooks/useSeo";

const VERDICT_FILTERS = [
  { key: "all", label: "全部结论" },
  { key: "verified", label: "已实锤" },
  { key: "uncertain", label: "待考证" },
  { key: "debunked", label: "已辟谣" },
];

export default function Topics() {
  useSeo(
    "事件解析 · 免费爆料真实性核查与辟谣 · 辨真社",
    "浏览辨真社全部事件核查：覆盖社会热点、吃瓜娱乐、电商带货、职场内幕与科技圈，用证据链分级为实锤、存疑与已辟谣，帮你判断爆料真伪。"
  );
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const cat = params.get("c") ?? "all";
  const verdict = params.get("v") ?? "all";

  const list = useMemo(() => {
    let res = facts;
    const kw = (params.get("q") ?? "").trim().toLowerCase();
    if (cat !== "all") res = res.filter((a) => a.category === cat);
    if (verdict !== "all") res = res.filter((a) => a.verdict === verdict);
    if (kw)
      res = res.filter((a) =>
        (a.title + a.claim + a.summary + categoryName(a.category)).toLowerCase().includes(kw)
      );
    return res;
  }, [cat, verdict, params]);

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "all" || v === "") next.delete(k);
      else next.set(k, v);
    });
    setParams(next, { replace: true });
  };

  const doSearch = (e: React.FormEvent) => {
    e.preventDefault();
    update({ q: q.trim() });
  };

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "事件解析与爆料核验",
          url: window.location.origin + window.location.pathname,
          inLanguage: "zh-CN",
          itemListElement: facts.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: a.title,
          })),
        }}
      />
      <Navbar />
      <main>
        {/* 页头 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <Breadcrumb
              items={[{ label: "首页", to: "/" }, { label: "事件解析" }]}
            />
            <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-5xl">
                  事件解析中心
                </h1>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-foreground-700">
                  海量免费爆料真假难辨？这里汇总每一起核验的瓜料，从证据链、来源溯源到动机分析，
                  为每一条事件标注清晰的可信度结论。
                </p>
              </div>
              <form onSubmit={doSearch} className="relative w-full lg:w-80">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-foreground-500" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  type="text"
                  placeholder="搜索事件、关键词…"
                  className="h-11 w-full rounded-md border border-background-200 bg-background-50 pl-10 pr-3 text-sm outline-none transition-colors focus:border-secondary-400"
                />
              </form>
            </div>

            {/* 分类筛选 */}
            <div className="mt-8 flex flex-wrap gap-2.5">
              <button
                onClick={() => update({ c: "all" })}
                className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  cat === "all"
                    ? "bg-foreground-950 text-background-50"
                    : "border border-background-200 bg-background-50 text-foreground-700 hover:border-secondary-300"
                }`}
              >
                全部分类
              </button>
              {categories.map((c) => (
                <button
                  key={c.key}
                  onClick={() => update({ c: c.key })}
                  className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                    cat === c.key
                      ? "bg-foreground-950 text-background-50"
                      : "border border-background-200 bg-background-50 text-foreground-700 hover:border-secondary-300"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            {/* 真伪状态筛选 */}
            <div className="mt-3 inline-flex flex-wrap gap-1.5 rounded-full border border-background-200 bg-background-50 p-1">
              {VERDICT_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => update({ v: f.key })}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    verdict === f.key
                      ? "bg-primary-600 text-background-50"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* 结果 */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <div className="mb-6 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
            <span>共找到 <strong className="text-foreground-950">{list.length}</strong> 起相关核验</span>
            {cat !== "all" && (
              <span className="inline-flex items-center gap-1 rounded-md bg-secondary-100 px-2 py-1 text-xs text-secondary-900">
                {categoryName(cat)} <button onClick={() => update({ c: "all" })}><i className="ri-close-line" /></button>
              </span>
            )}
            {verdict !== "all" && (
              <span className="inline-flex items-center gap-1 rounded-md bg-secondary-100 px-2 py-1 text-xs text-secondary-900">
                {VERDICT_FILTERS.find((v) => v.key === verdict)?.label}
                <button onClick={() => update({ v: "all" })}><i className="ri-close-line" /></button>
              </span>
            )}
          </div>

          {list.length === 0 ? (
            <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-100 py-20 text-center">
              <i className="ri-inbox-2-line text-5xl text-foreground-300" />
              <p className="mt-4 text-base text-foreground-600">没有找到匹配的事件，换个关键词或筛选条件试试。</p>
              <button
                onClick={() => {
                  setQ("");
                  setParams(new URLSearchParams(), { replace: true });
                }}
                className="mt-5 inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-foreground-950 px-5 text-sm font-medium text-background-50"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          )}
        </section>

        {/* 图例说明 */}
        <section className="border-t border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950">如何看懂核验结论</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              {(Object.keys(verdictMeta) as Array<"verified" | "uncertain" | "debunked">).map((k) => (
                <div key={k} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <VerdictBadge verdict={k} />
                  <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                    {k === "verified" && "证据链完整、来源可溯、多方对撞一致，事件可基本认定为真。"}
                    {k === "uncertain" && "存在争议但证据不足，既无法证实也难以证伪，需等待更多公开信息。"}
                    {k === "debunked" && "经溯源与多方核验，确认内容失实或系摆拍、合成、夸大，结论为假。"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}